import { Constants } from "../models/Constants";
import { DataSourceModel } from "../models/DQA/DataSourceModel";
import {
  ServiceLevelFinalizeModel,
  ServiceLevelRequestModel,
  ServiceLevelDeleteModel,
} from "../models/DQA/ServiceLevelRequestModel";
import {
  FinalizeObservedDataQualityReasonModel,
  ServiceLevelObservedDataQualityReasonModel,
  ServiceLevelSummaryModel,
} from "../models/DQA/ServiceLevelSummaryModel";
import { BaseMessageModel, ErrorModel } from "../models/ErrorModel";
import { VariableModel } from "../models/VariableModel";
import { deleteCall, getCall, patchCall, postCall, putCall } from "./api";
import { instance } from "./instance";
import { notificationService } from "./notificationService";
import i18next from "../i18n";
import { StatusCode } from "../models/Enums";
import { IndicatorReportModel } from "../models/DataAnalysis/DeskReview/IndicatorReportDetailsModel";
import { IndicatorModel } from "../models/DQA/IndicatorModel";
import {
  DeskLevelFinalizeModel,
  DeskLevelRequestModel,
  DLDQARequestModel,
} from "../models/DQA/DeskLevelRequestModel";
import {
  EliminationSummaryModel,
  SummaryModel,
} from "../models/DQA/EliminationSummaryModel";
import MultiSelectModel from "../models/MultiSelectModel";
import {
  DeskLevelSummaryReportModel,
  SummaryDataModel,
} from "../models/DQA/DeskLevelSummaryReportModel";
import { RetrieveMultipleRecords } from "../models/RetrieveMultipleRecords";

/** Handles all responsibilities related to DQA */
export const dqaService = {
  /** DQA SL Export Excel (async background job)
   * @param serviceLevelId The service level ID to export
   * @param onProgress Optional callback called with translated step label on each progress update
   * @param onAllComplete Optional callback called when all steps are complete
   */
  exportServiceLevelExcelAsync: async (
    serviceLevelId: string,
    onProgress?: (step: string) => void,
    onAllComplete?: () => void
  ): Promise<Blob> => {
    onProgress?.(`${i18next.t("Common.Exporting")}...`);

    const startUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.DQA_SL_EXPORT_START.replace("{0}", serviceLevelId)}`;
    const jobResponse = await instance.post(startUrl, null, { skipGlobalLoader: true } as any);
    const jobId: string = jobResponse?.data?.jobId;
    if (!jobId) throw new Error(i18next.t("Errors.SomethingWentWrong") as string);

    const maxAttempts = 900;
    const pollIntervalMs = 2000;
    const stepVisibleMs = 1000;

    const BACKEND_STEPS = ["", "Fetching data...", "Generating file..."];
    const stepTranslations: Record<string, string> = {
      "Fetching data...":   i18next.t("Common.ExportStepFetchingData") as string,
      "Generating file...": i18next.t("Common.ExportStepGeneratingFile") as string,
    };

    let lastShownIndex = 0;
    const animateToStep = async (targetIndex: number) => {
      for (let i = lastShownIndex + 1; i <= targetIndex; i++) {
        onProgress?.(stepTranslations[BACKEND_STEPS[i]] ?? BACKEND_STEPS[i]);
        await new Promise(resolve => setTimeout(resolve, stepVisibleMs));
      }
      lastShownIndex = Math.max(lastShownIndex, targetIndex);
    };

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));

      const statusUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.DQA_SL_EXPORT_STATUS.replace("{0}", jobId)}`;
      const statusResponse = await instance.get(statusUrl, { skipGlobalLoader: true } as any);
      const { status, currentStep, errorMessage } = statusResponse.data;

      if (currentStep) {
        const stepIndex = BACKEND_STEPS.indexOf(currentStep);
        if (stepIndex > lastShownIndex) await animateToStep(stepIndex);
      }

      if (status === "Succeeded") {
        await animateToStep(BACKEND_STEPS.length - 1);
        onAllComplete?.();
        await new Promise(resolve => setTimeout(resolve, 800));

        const downloadUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.DQA_SL_EXPORT_DOWNLOAD.replace("{0}", jobId)}`;
        const downloadResponse = await instance.get(downloadUrl, { responseType: "blob", skipGlobalLoader: true } as any);
        return downloadResponse.data as Blob;
      }

      if (status === "Failed") {
        const msg = errorMessage || i18next.t("Errors.SomethingWentWrong");
        notificationService.sendMessage(new BaseMessageModel(
          i18next.t("Errors.SomethingWentWrong"),
          StatusCode.InternalServerError
        ));
        throw new Error(msg as string);
      }
    }

    throw new Error("Export timed out after 30 minutes.");
  },

  /** DQA SL File Upload */
  fileUploadResponse: (request: FileList) =>
    new Promise<boolean>((resolve, reject) => {
      let formData = new FormData();
      formData.append("file", request[0]);
      postCall<any>(Constants.Api.Url.UPLOAD_SL_DQA_EXCEL_FILE, formData)
        .then((response: any) => {
          resolve(response);
          if (response) {
          }
        })
        .catch(error => reject(error));
    }),

  /** Get a response of a variables for DQA
   *  @param assessmentStrategyId A string value
   *   @param isSystemDefined A boolean value
   */
  getDQACoreVariables: (
    assessmentStrategyId: string,
    isSystemDefined: boolean
  ) =>
    new Promise<Array<VariableModel>>((resolve, reject) => {
      const url =
        Constants.Api.Url.DATA_COLLECTION_DQA_VARIABLES_GET_RESPONSE.replace(
          "{0}",
          assessmentStrategyId
        ).replace("{1}", String(isSystemDefined));

      getCall(url).then((response: any) => {
        if (response) {
          resolve(response);
        } else {
          resolve([]);
        }
      });
    }),

  /** Get Data Sources for DQA*/
  getDataSources: () =>
    new Promise<Array<DataSourceModel>>((resolve, reject) => {
      getCall(Constants.Api.Url.DQA_DATASOURCES_GET_RESPONSE).then(
        (response: Array<DataSourceModel>) => {
          resolve(response);
        }
      );
    }),

  /** Save Service Level DQA Response */
  saveServiceLevelDQA: (request: ServiceLevelRequestModel) =>
    new Promise<boolean>((resolve, reject) => {
      postCall<ServiceLevelRequestModel>(
        Constants.Api.Url.DQA_SERVICE_LEVEL_SAVE_RESPONSE,
        request
      ).then(response => {
        resolve(response);
        if (response) {
          notificationService.sendMessage(
            new BaseMessageModel(
              i18next.t(
                "Assessment.DataCollection.DataQualityAssessment.Message.SaveServiceLevelSuccessMessage"
              ),
              StatusCode.Created
            )
          );
        } else {
          notificationService.sendMessage(
            new BaseMessageModel(
              i18next.t(
                "Assessment.DataCollection.DataQualityAssessment.Message.SaveServiceLevelErrorMessage"
              ),
              StatusCode.PreConditionFailed
            )
          );
        }
      });
    }),

  /** Update Service Level DQA Response */
  updateServiceLevelDQA: (request: ServiceLevelRequestModel) =>
    new Promise((resolve, reject) => {
      putCall<ServiceLevelRequestModel>(
        Constants.Api.Url.DQA_SERVICE_LEVEL_UPDATE_RESPONSE,
        request
      )
        .then(response => {
          if (response == "") {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.DataCollection.DataQualityAssessment.Message.UpdateServiceLevelSuccessMessage"
                ),
                StatusCode.Created
              )
            );
          } else {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.DataCollection.DataQualityAssessment.Message.UpdateServiceLevelErrorMessage"
                ),
                StatusCode.PreConditionFailed
              )
            );
          }
          resolve("");
        })
        .catch((error: ErrorModel) => {
          if (error && error.statusCode !== StatusCode.PreConditionFailed) {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t("Errors.SomethingWentWrong"),
                StatusCode.InternalServerError
              )
            );
          }
          reject(true);
        });
    }),

  /** Get Service  Level DQA Response  by assessmentId
   *  @param assessmentId A string value
   */
  getServiceLevelData: (assessmentId: string) =>
    new Promise<ServiceLevelRequestModel>((resolve, reject) => {
      getCall(
        Constants.Api.Url.DQA_SERVICE_LEVEL_GET_RESPONSE.replace(
          "{0}",
          assessmentId
        )
      ).then((response: ServiceLevelRequestModel) => {
        if (response != null) {
          resolve(response);
        } else {
          resolve(ServiceLevelRequestModel.init(assessmentId));
        }
      });
    }),

  /** Finalize Service Level DQA Response */
  finalizeServiceLevel: (request: ServiceLevelFinalizeModel) =>
    new Promise((resolve, reject) => {
      putCall<ServiceLevelFinalizeModel>(
        Constants.Api.Url.DQA_SERVICE_LEVEL_FINALIZE_RESPONSE,
        request
      ).then(response => {
        if (response) {
          notificationService.sendMessage(
            new BaseMessageModel(
              i18next.t(
                "Assessment.DataCollection.DataQualityAssessment.Message.FinalizeServiceLevelSuccessMessage"
              ),
              StatusCode.Created
            )
          );
        } else {
          notificationService.sendMessage(
            new BaseMessageModel(
              i18next.t(
                "Assessment.DataCollection.DataQualityAssessment.Message.FinalizeServiceLevelErrorMessage"
              ),
              StatusCode.PreConditionFailed
            )
          );
        }
        resolve(response);
      });
    }),

  /** Delete Service Level DQA Response */
  deleteServiceLevel: (request: ServiceLevelDeleteModel) =>
    new Promise((resolve, reject) => {
      deleteCall<ServiceLevelDeleteModel>(
        Constants.Api.Url.DQA_SERVICE_LEVEL_DELETE_RESPONSE,
        request
      ).then(response => {
        if (response) {
          notificationService.sendMessage(
            new BaseMessageModel(
              i18next.t(
                "Assessment.DataCollection.DataQualityAssessment.Message.DeleteServiceLevelSuccessMessage"
              ),
              StatusCode.Created
            )
          );
        } else {
          notificationService.sendMessage(
            new BaseMessageModel(
              i18next.t(
                "Assessment.DataCollection.DataQualityAssessment.Message.DeleteServiceLevelErrorMessage"
              ),
              StatusCode.PreConditionFailed
            )
          );
        }
        resolve(response);
      });
    }),

  /** Generate SL DQA Template
   *  @param serviceLevelId A string value
   */
  generateTemplateServiceLevelDQA: (serviceLevelId: string) =>
    new Promise<boolean>((resolve, reject) => {
      const url = Constants.Api.Url.EXPORT_SL_DQA_EXCEL_FILE.replace(
        "{0}",
        serviceLevelId
      );
      console.log("DQA Service Level Template Generation - URL:", url);
      getCall(url, "blob")
        .then((response: any) => {
          console.log("DQA Service Level Template Generation - Success");
          resolve(response);
        })
        .catch((error: ErrorModel) => {
          console.error(
            "DQA Service Level Template Generation - Error:",
            error
          );
          if (error && error.statusCode !== StatusCode.PreConditionFailed) {
            const errorMessage =
              error.text || i18next.t("Errors.SomethingWentWrong");
            notificationService.sendMessage(
              new BaseMessageModel(
                `${i18next.t("Assessment.DataCollection.DataQualityAssessment.Message.ServiceLevelTemplateGenerationError")}: ${errorMessage}`,
                StatusCode.InternalServerError
              )
            );
          }
          reject(error);
        });
    }),

  /** Upload Service Level Template — returns 202 Accepted then polls until processing completes.
   *  @param onProgress Optional callback invoked with a human-readable step message at each stage.
   *  @param onAllComplete Optional callback invoked once all steps are complete.
   */
  uploadServiceLevelTemplateResponse: async (
    request: FormData,
    onProgress?: (step: string) => void,
    onAllComplete?: () => void
  ): Promise<boolean> => {
    // Step 1: submit the upload; server queues the job (returns 202)
    onProgress?.(`${i18next.t("Common.Uploading")}...`);
    const jobResponse: any = await postCall<FormData>(
      Constants.Api.Url.UPLOAD_SL_DQA_EXCEL_FILE,
      request
    );

    const jobId: string = jobResponse?.jobId;
    if (!jobId) {
      throw new Error(i18next.t("Errors.SomethingWentWrong") as string);
    }

    // Step 2: poll status until succeeded / failed (max 60 minutes, 2-second intervals)
    const maxAttempts    = 1800;
    const pollIntervalMs = 2000;
    const stepVisibleMs  = 1000;

    const BACKEND_STEPS = [
      "",
      "Parsing Excel data...",
      "Calculating results...",
      "Saving to database...",
    ];
    const stepTranslations: Record<string, string> = {
      "Parsing Excel data...":  i18next.t("Common.UploadStepParsingExcel") as string,
      "Calculating results...": i18next.t("Common.UploadStepCalculating") as string,
      "Saving to database...":  i18next.t("Common.UploadStepSaving") as string,
    };

    let lastShownIndex = 0;

    const animateToStep = async (targetIndex: number) => {
      for (let i = lastShownIndex + 1; i <= targetIndex; i++) {
        onProgress?.(stepTranslations[BACKEND_STEPS[i]] ?? BACKEND_STEPS[i]);
        await new Promise(resolve => setTimeout(resolve, stepVisibleMs));
      }
      lastShownIndex = Math.max(lastShownIndex, targetIndex);
    };

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));

      const statusUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.DQA_SL_UPLOAD_FILE_STATUS.replace("{0}", jobId)}`;
      const statusResponse = await instance.get(statusUrl, { skipGlobalLoader: true } as any);
      const { status, currentStep, errorMessage } = statusResponse.data;

      if (currentStep) {
        const stepIndex = BACKEND_STEPS.indexOf(currentStep);
        if (stepIndex > lastShownIndex) {
          await animateToStep(stepIndex);
        }
      }

      if (status === "Succeeded") {
        await animateToStep(BACKEND_STEPS.length - 1);
        onAllComplete?.();
        await new Promise(resolve => setTimeout(resolve, 800));
        notificationService.sendMessage(
          new BaseMessageModel(
            i18next.t("Assessment.DataCollection.DataQualityAssessment.Message.ServiceLevelUploadTemplateSuccessMessage"),
            StatusCode.Created
          )
        );
        return true;
      }

      if (status === "Failed") {
        const msg = errorMessage || i18next.t("Errors.SomethingWentWrong");
        notificationService.sendMessage(
          new BaseMessageModel(
            `${i18next.t("Assessment.DataCollection.DataQualityAssessment.Message.ServiceLevelUploadTemplateErrorMessage")}: ${msg}`,
            StatusCode.PreConditionFailed
          )
        );
        throw new Error(msg as string);
      }
      // Queued | Processing → keep polling
    }

    const timeoutMsg = i18next.t("Errors.SomethingWentWrong") as string;
    notificationService.sendMessage(
      new BaseMessageModel(timeoutMsg, StatusCode.InternalServerError)
    );
    throw new Error("File processing timed out after 60 minutes.");
  },

  /** Get Service  Level Summary DQA Response  by assessmentId
   *  @param assessmentId A string value
   */
  getServiceLevelSummary: (assessmentId: string) =>
    new Promise<ServiceLevelSummaryModel>((resolve, reject) => {
      getCall(
        Constants.Api.Url.DQA_SERVICE_LEVEL_SUMMARY_GET_RESPONSE.replace(
          "{0}",
          assessmentId
        )
      ).then((response: ServiceLevelSummaryModel) => {
        if (response != null) {
          resolve(response);
        } else {
          resolve(ServiceLevelSummaryModel.init());
        }
      });
    }),

  /** Update the reasons for the observed data quality results for Service Level DQA */
  updateServiceLevelObservedDataQualityReason: (
    request: ServiceLevelObservedDataQualityReasonModel
  ) =>
    new Promise((resolve, reject) => {
      patchCall<ServiceLevelObservedDataQualityReasonModel>(
        Constants.Api.Url
          .DQA_SERVICE_LEVEL_UPDATE_OBSERVED_DATA_QUALITY_REASON_RESPONSE,
        request
      )
        .then(response => {
          if (response) {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.DataCollection.DataQualityAssessment.Message.UpdateObservedDataQualitySuccessMessage"
                ),
                StatusCode.Created
              )
            );
          } else {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.DataCollection.DataQualityAssessment.Message.UpdateObservedDataQualityErrorMessage"
                ),
                StatusCode.PreConditionFailed
              )
            );
          }
          resolve(response);
        })
        .catch((error: ErrorModel) => {
          if (error && error.statusCode !== StatusCode.PreConditionFailed) {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t("Errors.SomethingWentWrong"),
                StatusCode.InternalServerError
              )
            );
          }
          reject(true);
        });
    }),

  /** Finalize the reasons for the observed data quality results for Service Level DQA */
  finalizeObservedDataQualityResultReason: (
    request: FinalizeObservedDataQualityReasonModel
  ) =>
    new Promise((resolve, reject) => {
      patchCall<FinalizeObservedDataQualityReasonModel>(
        Constants.Api.Url
          .DQA_SERVICE_LEVEL_FINALIZE_OBSERVED_DATA_QUALITY_REASON_RESPONSE,
        request
      )
        .then(response => {
          if (response) {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.DataCollection.DataQualityAssessment.Message.FinalizeObservedDataQualitySuccessMessage"
                ),
                StatusCode.Created
              )
            );
          } else {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.DataCollection.DataQualityAssessment.Message.FinalizeObservedDataQualityErrorMessage"
                ),
                StatusCode.PreConditionFailed
              )
            );
          }
          resolve(response);
        })
        .catch((error: ErrorModel) => {
          if (error && error.statusCode !== StatusCode.PreConditionFailed) {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t("Errors.SomethingWentWrong"),
                StatusCode.InternalServerError
              )
            );
          }
          reject(true);
        });
    }),

  /** Get a response for indicators report for DL DQA
   *  @param indicator A integer value for the indicator report type
   *  @param assessmentId A boolean value
   */
  getDeskLevelReport: (indicatorReportType: number, assessmentId: string) =>
    new Promise<IndicatorReportModel>((resolve, reject) => {
      const url = Constants.Api.Url.DQA_DESK_LEVEL_REPORT.replace(
        "{0}",
        indicatorReportType.toString()
      ).replace("{1}", assessmentId);

      getCall(url)
        .then((response: any) => {
          if (response) {
            resolve(response);
          } else {
            resolve(IndicatorReportModel.init());
          }
        })
        .catch(reject);
    }),

  /** Save Desk Level DQA Response */
  saveDeskLevelDQA: (request: DLDQARequestModel) =>
    new Promise<boolean>((resolve, reject) => {
      postCall<DLDQARequestModel>(
        Constants.Api.Url.DQA_DESK_LEVEL_SAVE_RESPONSE,
        request
      ).then(response => {
        resolve(response);
        if (response) {
          notificationService.sendMessage(
            new BaseMessageModel(
              i18next.t(
                "Assessment.DataCollection.DataQualityAssessment.Message.SaveDeskLevelSuccessMessage"
              ),
              StatusCode.Created
            )
          );
        } else {
          notificationService.sendMessage(
            new BaseMessageModel(
              i18next.t(
                "Assessment.DataCollection.DataQualityAssessment.Message.SaveDeskLevelErrorMessage"
              ),
              StatusCode.PreConditionFailed
            )
          );
        }
      });
    }),

  /** Update Desk Level DQA Response */
  updateDeskLevelDQA: (request: DLDQARequestModel) =>
    new Promise((resolve, reject) => {
      postCall<DLDQARequestModel>(
        Constants.Api.Url.DQA_DESK_LEVEL_SAVE_RESPONSE,
        request
      )
        .then(response => {
          if (response) {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.DataCollection.DataQualityAssessment.Message.UpdateDeskLevelSuccessMessage"
                ),
                StatusCode.Created
              )
            );
          } else {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.DataCollection.DataQualityAssessment.Message.UpdateDeskLevelErrorMessage"
                ),
                StatusCode.PreConditionFailed
              )
            );
          }
          resolve("");
        })
        .catch((error: ErrorModel) => {
          if (error && error.statusCode !== StatusCode.PreConditionFailed) {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t("Errors.SomethingWentWrong"),
                StatusCode.InternalServerError
              )
            );
          }
          reject(true);
        });
    }),

  /** Get Desk Level DQA Response by assessmentId
   *  @param assessmentId A string value
   */
  getDeskLevelDQA: (assessmentId: string) =>
    new Promise<DeskLevelRequestModel>((resolve, reject) => {
      getCall(
        Constants.Api.Url.DQA_DESK_LEVEL_GET_RESPONSE.replace(
          "{0}",
          assessmentId
        ),
        "json",
        false
      )
        .then((response: DeskLevelRequestModel) => {
          if (response != null) {
            resolve(response);
          } else {
            resolve(DeskLevelRequestModel.init(assessmentId));
          }
        })
        .catch((error: ErrorModel) => {
          if (error && error.statusCode !== StatusCode.PreConditionFailed) {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t("Errors.SomethingWentWrong"),
                StatusCode.InternalServerError
              )
            );
          }
          reject(true);
        });
    }),

  /** Finalize Desk Level DQA Response */
  finalizeDeskLevel: (request: DeskLevelFinalizeModel) =>
    new Promise((resolve, reject) => {
      postCall<DeskLevelFinalizeModel>(
        Constants.Api.Url.DQA_DESK_LEVEL_FINALIZE_RESPONSE,
        request
      ).then(response => {
        if (response) {
          notificationService.sendMessage(
            new BaseMessageModel(
              i18next.t(
                "Assessment.DataCollection.DataQualityAssessment.Message.FinalizeDeskLevelSuccessMessage"
              ),
              StatusCode.Created
            )
          );
        } else {
          notificationService.sendMessage(
            new BaseMessageModel(
              i18next.t(
                "Assessment.DataCollection.DataQualityAssessment.Message.FinalizeDeskLevelErrorMessage"
              ),
              StatusCode.PreConditionFailed
            )
          );
        }
        resolve(response);
      });
    }),

  /** Generate DL DQA Template
   *  @param assessmentId A string value
   */
  generateTemplateDeskLevelDQA: (assessmentId: string) =>
    new Promise<boolean>((resolve, reject) => {
      const url = Constants.Api.Url.GENERATE_DL_DQA_EXCEL_FILE.replace(
        "{0}",
        assessmentId
      );
      console.log("DQA Desk Level Template Generation - URL:", url);
      getCall(url, "blob")
        .then((response: any) => {
          console.log("DQA Desk Level Template Generation - Success");
          resolve(response);
        })
        .catch((error: ErrorModel) => {
          console.error("DQA Desk Level Template Generation - Error:", error);
          if (error && error.statusCode !== StatusCode.PreConditionFailed) {
            const errorMessage =
              error.text || i18next.t("Errors.SomethingWentWrong");
            notificationService.sendMessage(
              new BaseMessageModel(
                `${i18next.t("Assessment.DataCollection.DataQualityAssessment.Message.DeskLevelTemplateGenerationError")}: ${errorMessage}`,
                StatusCode.InternalServerError
              )
            );
          }
          reject(error);
        });
    }),

  /** Upload Desk Level Template — returns 202 Accepted then polls until processing completes.
   *  @param onProgress Optional callback invoked with a human-readable step message at each stage.
   */
  uploadDeskLevelTemplateResponse: async (
    request: FormData,
    onProgress?: (step: string) => void,
    onAllComplete?: () => void
  ): Promise<boolean> => {
    // Step 1: submit the file upload; server scans for viruses then queues the job (returns 202)
    onProgress?.(`${i18next.t("Common.Uploading")}...`);
    const jobResponse: any = await postCall<FormData>(
      Constants.Api.Url.UPLOAD_DL_DQA_EXCEL_FILE,
      request
    );

    const jobId: string = jobResponse?.jobId;
    if (!jobId) {
      throw new Error(i18next.t("Errors.SomethingWentWrong") as string);
    }

    // Step 2: poll status until succeeded / failed (max 60 minutes, 2-second intervals)
    const maxAttempts = 1800;
    const pollIntervalMs = 2000;
    const stepVisibleMs = 1000; // minimum time each step stays visible

    // Ordered backend step keys (index 0 = "Uploading" placeholder, not a backend key)
    const BACKEND_STEPS = [
      "",
      "Parsing Excel data...",
      "Calculating results...",
      "Saving to database...",
    ];
    const stepTranslations: Record<string, string> = {
      "Parsing Excel data...":  i18next.t("Common.UploadStepParsingExcel") as string,
      "Calculating results...": i18next.t("Common.UploadStepCalculating") as string,
      "Saving to database...":  i18next.t("Common.UploadStepSaving") as string,
    };

    // Tracks the last step index shown to the user so we never skip one
    let lastShownIndex = 0;

    // Animate forward through steps up to targetIndex, holding each for stepVisibleMs
    const animateToStep = async (targetIndex: number) => {
      for (let i = lastShownIndex + 1; i <= targetIndex; i++) {
        onProgress?.(stepTranslations[BACKEND_STEPS[i]] ?? BACKEND_STEPS[i]);
        await new Promise(resolve => setTimeout(resolve, stepVisibleMs));
      }
      lastShownIndex = Math.max(lastShownIndex, targetIndex);
    };

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));

      const statusUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.DQA_DESK_LEVEL_PROCESS_FILE_STATUS.replace("{0}", jobId)}`;
      const statusResponse = await instance.get(statusUrl, { skipGlobalLoader: true } as any);
      const { status, currentStep, errorMessage } = statusResponse.data;

      // Advance the stepper to the step the backend is currently on
      if (currentStep) {
        const stepIndex = BACKEND_STEPS.indexOf(currentStep);
        if (stepIndex > lastShownIndex) {
          await animateToStep(stepIndex);
        }
      }

      if (status === "Succeeded") {
        // Animate through any steps not yet reached, then hold all-complete for 800 ms
        await animateToStep(BACKEND_STEPS.length - 1);
        onAllComplete?.();
        await new Promise(resolve => setTimeout(resolve, 800));
        notificationService.sendMessage(
          new BaseMessageModel(
            i18next.t(
              "Assessment.DataCollection.DataQualityAssessment.Message.DeskLevelUploadTemplateSuccessMessage"
            ),
            StatusCode.Created
          )
        );
        return true;
      }

      if (status === "Failed") {
        const msg = errorMessage || i18next.t("Errors.SomethingWentWrong");
        notificationService.sendMessage(
          new BaseMessageModel(
            `${i18next.t("Assessment.DataCollection.DataQualityAssessment.Message.DeskLevelUploadTemplateErrorMessage")}: ${msg}`,
            StatusCode.PreConditionFailed
          )
        );
        throw new Error(msg as string);
      }

      // status is Queued or Processing — keep polling
    }

    const timeoutMsg = i18next.t("Errors.SomethingWentWrong") as string;
    notificationService.sendMessage(
      new BaseMessageModel(timeoutMsg, StatusCode.InternalServerError)
    );
    throw new Error("File processing timed out after 60 minutes.");
  },

  /** Get Indicators list for desk level data quality assessment by assessment id*/
  getDeskLevelIndicators: () =>
    new Promise<Array<IndicatorModel>>((resolve, reject) => {
      getCall(Constants.Api.Url.DQA_DESK_LEVEL_INDICATORS).then(indicators => {
        if (indicators) {
          resolve(indicators);
        }
      });
    }),

  /** Download desk level report (async background job)
   * @param assessmentId A string value
   * @param onProgress Optional callback called with translated step label on each progress update
   * @param onAllComplete Optional callback called when all steps are complete
   */
  downloadReport: async (
    assessmentId: string,
    onProgress?: (step: string) => void,
    onAllComplete?: () => void
  ): Promise<Blob> => {
    onProgress?.(`${i18next.t("Common.Exporting")}...`);

    const startUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.DQA_DESK_LEVEL_REPORT_EXPORT_START.replace("{0}", assessmentId)}`;
    const jobResponse = await instance.post(startUrl, null, { skipGlobalLoader: true } as any);
    const jobId: string = jobResponse?.data?.jobId;
    if (!jobId) throw new Error(i18next.t("Errors.SomethingWentWrong") as string);

    const maxAttempts = 900;
    const pollIntervalMs = 2000;
    const stepVisibleMs = 1000;

    const BACKEND_STEPS = ["", "Fetching data...", "Generating file..."];
    const stepTranslations: Record<string, string> = {
      "Fetching data...":   i18next.t("Common.ExportStepFetchingData") as string,
      "Generating file...": i18next.t("Common.ExportStepGeneratingFile") as string,
    };

    let lastShownIndex = 0;
    const animateToStep = async (targetIndex: number) => {
      for (let i = lastShownIndex + 1; i <= targetIndex; i++) {
        onProgress?.(stepTranslations[BACKEND_STEPS[i]] ?? BACKEND_STEPS[i]);
        await new Promise(resolve => setTimeout(resolve, stepVisibleMs));
      }
      lastShownIndex = Math.max(lastShownIndex, targetIndex);
    };

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));

      const statusUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.DQA_DESK_LEVEL_REPORT_EXPORT_STATUS.replace("{0}", jobId)}`;
      const statusResponse = await instance.get(statusUrl, { skipGlobalLoader: true } as any);
      const { status, currentStep, errorMessage } = statusResponse.data;

      if (currentStep) {
        const stepIndex = BACKEND_STEPS.indexOf(currentStep);
        if (stepIndex > lastShownIndex) await animateToStep(stepIndex);
      }

      if (status === "Succeeded") {
        await animateToStep(BACKEND_STEPS.length - 1);
        onAllComplete?.();
        await new Promise(resolve => setTimeout(resolve, 800));

        const downloadUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.DQA_DESK_LEVEL_REPORT_EXPORT_DOWNLOAD.replace("{0}", jobId)}`;
        const downloadResponse = await instance.get(downloadUrl, { responseType: "blob", skipGlobalLoader: true } as any);
        return downloadResponse.data as Blob;
      }

      if (status === "Failed") {
        const msg = errorMessage || i18next.t("Errors.SomethingWentWrong");
        notificationService.sendMessage(new BaseMessageModel(
          i18next.t("Errors.SomethingWentWrong"),
          StatusCode.InternalServerError
        ));
        throw new Error(msg as string);
      }
    }

    throw new Error("Export timed out after 30 minutes.");
  },

  /** Elimination DQA export excel */
  exportEliminationExcelTemplate: () =>
    new Promise<boolean>((resolve, reject) => {
      const url = Constants.Api.Url.ELIMINATION_DQA_EXPORT;
      getCall(url, "blob")
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: ErrorModel) => {
          if (error && error.statusCode !== StatusCode.PreConditionFailed) {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t("Errors.SomethingWentWrong"),
                StatusCode.InternalServerError
              )
            );
          }
          reject(true);
        });
    }),

  /** Save summary result for Elimination DQA */
  saveEliminationDQASummary: (request: SummaryModel) =>
    new Promise<boolean>((resolve, reject) => {
      postCall<SummaryModel>(
        Constants.Api.Url.SAVE_ELIMINATION_DQA_SUMMARY,
        request
      )
        .then(response => {
          resolve(response);
          if (response) {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.DataCollection.DataQualityAssessment.Message.SaveEliminationDQASuccessMessage"
                ),
                StatusCode.Created
              )
            );
          } else {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.DataCollection.DataQualityAssessment.Message.SaveEliminationDQAErrorMessage"
                ),
                StatusCode.PreConditionFailed
              )
            );
          }
        })
        .catch((error: ErrorModel) => {
          console.error("Elimination DQA Save - Error:", error);
          if (error && error.statusCode !== StatusCode.PreConditionFailed) {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t("Errors.SomethingWentWrong"),
                StatusCode.InternalServerError
              )
            );
          }
          reject(error);
        });
    }),

  /** Get Elimination DQA Summary Response by assessmentId and year
   *  @param assessmentId A string value
   */
  getEliminationDQASummary: (assessmentId: string) =>
    new Promise<EliminationSummaryModel>((resolve, reject) => {
      getCall(
        Constants.Api.Url.ELIMINATION_DQA_GET_SUMMARY_RESPONSE.replace(
          "{0}",
          assessmentId
        )
      ).then((response: EliminationSummaryModel) => {
        if (response != null) {
          resolve(response);
        } else {
          resolve(EliminationSummaryModel.init(assessmentId));
        }
      });
    }),

  /** Finalize Elimination DQA Response */
  finalizeEliminationDQASummary: (request: SummaryModel) =>
    new Promise((resolve, reject) => {
      postCall<SummaryModel>(
        Constants.Api.Url.ELIMINATION_DQA_FINALIZE_RESPONSE,
        request
      ).then(response => {
        if (response) {
          notificationService.sendMessage(
            new BaseMessageModel(
              i18next.t(
                "Assessment.DataCollection.DataQualityAssessment.Message.FinalizeEliminationDQASuccessMessage"
              ),
              StatusCode.Created
            )
          );
        } else {
          notificationService.sendMessage(
            new BaseMessageModel(
              i18next.t(
                "Assessment.DataCollection.DataQualityAssessment.Message.FinalizeEliminationDQAErrorMessage"
              ),
              StatusCode.PreConditionFailed
            )
          );
        }
        resolve(response);
      });
    }),

  /** Generate elimination DQA report (async background job)
   * @param assessmentId A string value
   * @param onProgress Optional callback called with translated step label on each progress update
   * @param onAllComplete Optional callback called when all steps are complete
   */
  generateEliminationDQATemplate: async (
    assessmentId: string,
    onProgress?: (step: string) => void,
    onAllComplete?: () => void
  ): Promise<Blob> => {
    onProgress?.(`${i18next.t("Common.Exporting")}...`);

    const startUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.DQA_ELIMINATION_REPORT_EXPORT_START.replace("{0}", assessmentId)}`;
    const jobResponse = await instance.post(startUrl, null, { skipGlobalLoader: true } as any);
    const jobId: string = jobResponse?.data?.jobId;
    if (!jobId) throw new Error(i18next.t("Errors.SomethingWentWrong") as string);

    const maxAttempts = 900;
    const pollIntervalMs = 2000;
    const stepVisibleMs = 1000;

    const BACKEND_STEPS = ["", "Fetching data...", "Generating file..."];
    const stepTranslations: Record<string, string> = {
      "Fetching data...":   i18next.t("Common.ExportStepFetchingData") as string,
      "Generating file...": i18next.t("Common.ExportStepGeneratingFile") as string,
    };

    let lastShownIndex = 0;
    const animateToStep = async (targetIndex: number) => {
      for (let i = lastShownIndex + 1; i <= targetIndex; i++) {
        onProgress?.(stepTranslations[BACKEND_STEPS[i]] ?? BACKEND_STEPS[i]);
        await new Promise(resolve => setTimeout(resolve, stepVisibleMs));
      }
      lastShownIndex = Math.max(lastShownIndex, targetIndex);
    };

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));

      const statusUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.DQA_ELIMINATION_REPORT_EXPORT_STATUS.replace("{0}", jobId)}`;
      const statusResponse = await instance.get(statusUrl, { skipGlobalLoader: true } as any);
      const { status, currentStep, errorMessage } = statusResponse.data;

      if (currentStep) {
        const stepIndex = BACKEND_STEPS.indexOf(currentStep);
        if (stepIndex > lastShownIndex) await animateToStep(stepIndex);
      }

      if (status === "Succeeded") {
        await animateToStep(BACKEND_STEPS.length - 1);
        onAllComplete?.();
        await new Promise(resolve => setTimeout(resolve, 800));

        const downloadUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.DQA_ELIMINATION_REPORT_EXPORT_DOWNLOAD.replace("{0}", jobId)}`;
        const downloadResponse = await instance.get(downloadUrl, { responseType: "blob", skipGlobalLoader: true } as any);
        return downloadResponse.data as Blob;
      }

      if (status === "Failed") {
        const msg = errorMessage || i18next.t("Errors.SomethingWentWrong");
        notificationService.sendMessage(new BaseMessageModel(
          i18next.t("Errors.SomethingWentWrong"),
          StatusCode.InternalServerError
        ));
        throw new Error(msg as string);
      }
    }

    throw new Error("Export timed out after 30 minutes.");
  },

  /** Get Indicators list for desk level data quality assessment by assessment id*/
  getDeskLevelSummaryYears: (assessmentId: string) =>
    new Promise<Array<string>>((resolve, reject) => {
      getCall(
        Constants.Api.Url.DQA_DESK_LEVEL_SUMMARY_YEARS.replace(
          "{0}",
          assessmentId.toString()
        )
      ).then(response => {
        if (response) {
          resolve(response);
        }
      });
    }),

  /** Get Sumary result list for desk level data quality assessment by assessment id*/
  getDeskLevelSummaryResult: (assessmentId: string, year: string) =>
    new Promise<Array<SummaryDataModel>>((resolve, reject) => {
      getCall(
        Constants.Api.Url.DQA_DESK_LEVEL_NATIONAL_LEVEL_SUMMARY.replace(
          "{0}",
          assessmentId.toString()
        ).replace("{1}", year.toString())
      ).then(response => {
        if (response) {
          resolve(response);
        } else {
          resolve([SummaryDataModel.init(assessmentId)]);
        }
      });
    }),

  /** Saves the data analysis summary result by calling an API
   * request: an object matching the backend DTO structure
   */
  saveSummaryResult: (request: any) =>
    new Promise<boolean>((resolve, reject) => {
      postCall<any>(
        Constants.Api.Url.DQA_DESK_LEVEL_SAVE_NATIONAL_LEVEL_SUMMARY,
        request
      )
        .then(response => {
          resolve(response);
          if (response) {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.DataAnalysis.SaveDataAnalysisNationalSummaryResult"
                ),
                StatusCode.Created
              )
            );
          } else {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t("Errors.SomethingWentWrong"),
                StatusCode.PreConditionFailed
              )
            );
          }
        })
        .catch(error => reject(error));
    }),

  /** Finalize survey questions response */
  finalizeSummaryResult: (request: any) =>
    new Promise((resolve, reject) => {
      postCall<any>(
        Constants.Api.Url.DQA_DESK_LEVEL_FINALIZE_NATIONAL_LEVEL_SUMMARY,
        request
      )
        .then(response => {
          if (response) {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.DataAnalysis.FinalizedDataAnalysisNationalSummaryResult"
                ),
                StatusCode.Created
              )
            );
          } else {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.DataAnalysis.FinalizedDataAnalysisNationalSummaryResult"
                ),
                StatusCode.PreConditionFailed
              )
            );
          }
          resolve(response);
        })
        .catch(error => reject(error));
    }),

  /** Generate DL DQA Template
   *  @param assessmentId A string value
   */
  generateDeskLevelDQATemplate: (assessmentId: string) =>
    new Promise<boolean>((resolve, reject) => {
      const url = Constants.Api.Url.GENERATE_DESK_LEVEL_DQA_EXCEL_FILE.replace(
        "{0}",
        assessmentId
      );
      getCall(url, "blob")
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: ErrorModel) => {
          if (error && error.statusCode !== StatusCode.PreConditionFailed) {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t("Errors.SomethingWentWrong"),
                StatusCode.InternalServerError
              )
            );
          }
          reject(true);
        });
    }),
};
